import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trpc } from '@/utils/trpc';
import { useRealtimeTimestamps } from './realtime';
import { invalidateQueriesFromTimestamps, LastModification } from './timestampInvalidation';
export type { LastModification };
type UseLastModificationsOptions = {
  // AppShell fires this off the sticky-network localStorage value before
  // it's validated (see stickyNetworkSelection.ts) -- a stale/deleted
  // network id throws NOT_FOUND here every time until the self-heal effect
  // clears it a beat later. That's expected during the race there, not a
  // real failure (KBE-599) -- but every other caller passes a networkId
  // that's already validated (from the URL), where a failure here is real
  // and should still surface the global error toast. Opt-in, not default.
  silent?: boolean;
};
const useLastModifications = (networkId, options) => {
  const $ = _c(14);
  const trpcUtils = trpc.useUtils();
  let t0;
  if ($[0] !== networkId) {
    t0 = ["lastModification", networkId];
    $[0] = networkId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== networkId || $[3] !== trpcUtils) {
    t1 = async () => {
      const data = await trpcUtils.network.getTimestamps.fetch({
        networkId
      });
      invalidateQueriesFromTimestamps(networkId, data);
      return data;
    };
    $[2] = networkId;
    $[3] = trpcUtils;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = options?.silent ?? false;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      silent: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t1,
      placeholderData: keepPreviousData,
      staleTime: 0,
      meta: t3
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const query = useQuery(t4);
  let t5;
  if ($[11] !== networkId || $[12] !== query.isSuccess) {
    t5 = {
      networkId,
      enabled: query.isSuccess
    };
    $[11] = networkId;
    $[12] = query.isSuccess;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useRealtimeTimestamps(t5);
  return query;
};
export default useLastModifications;